"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  Calendar,
  FileCheck,
  Trophy,
  ArrowRight,
  Sparkles,
  Building2,
  Truck,
  Home,
  Stethoscope,
  Clock,
  CheckCircle2,
  TrendingUp,
  Play,
  RotateCcw,
  Mail,
  ShieldCheck,
} from "lucide-react";
import Link from "next/link";

interface IndustryConfig {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
  leadSample: {
    client: string;
    company: string;
    inquiry: string;
    dealSize: string;
  };
  stages: {
    title: string;
    badge: string;
    actionDescription: string;
    emailSubject: string;
    emailSnippet: string;
    metricLabel: string;
    metricValue: string;
  }[];
}

const INDUSTRIES: IndustryConfig[] = [
  {
    id: "tech",
    name: "B2B SaaS & Tech",
    icon: Building2,
    color: "from-blue-500 to-cyan-500",
    leadSample: {
      client: "Alex Rivera",
      company: "CloudScale Analytics",
      inquiry: "Custom Multi-Tenant Data Platform & AI Agent Integration",
      dealSize: "$28,000",
    },
    stages: [
      {
        title: "Autonomous Lead Capture",
        badge: "⚡ Ingested in 1.2s",
        actionDescription: "Lead captured from Google Search. Anti-spam filter passed, UTM campaign tagged, and deduped in SQLite engine.",
        emailSubject: "Thank you for contacting Johnson Softwares - Enquiry Received",
        emailSnippet: "Hello Alex, we have logged your inquiry regarding Custom Multi-Tenant Data Platform. Our architecture team is reviewing your requirements...",
        metricLabel: "Speed to Lead",
        metricValue: "< 45 Seconds",
      },
      {
        title: "Smart Discovery Call Trigger",
        badge: "📅 48h Calendar Alert",
        actionDescription: "Dispatched automated 4-point requirement gathering agenda. Video conference slot locked in with Tech Lead.",
        emailSubject: "Next Steps: Requirement Gathering & Discovery Call",
        emailSnippet: "We have mapped out your core user workflows and API prerequisites. Let's align on database topology and sprint milestones...",
        metricLabel: "Response Rate",
        metricValue: "96.4%",
      },
      {
        title: "Dynamic SOW & Proposal",
        badge: "📄 Tailored SOW Ready",
        actionDescription: "Interactive review modal generated fixed-price commercial breakdown: 3 phased milestones with 6-week MVP delivery.",
        emailSubject: "Engineering Proposal & Scope Formulation: CloudScale Analytics",
        emailSnippet: "We have formulated your milestone roadmap: Phase 1 Data Ingestion, Phase 2 AI Model Pipeline, Phase 3 Production Rollout...",
        metricLabel: "Proposal Velocity",
        metricValue: "4x Faster",
      },
      {
        title: "Deal Won & Kickoff Package",
        badge: "🎉 Milestone Paid",
        actionDescription: "Payment confirmed. Automated invoice JS-INV-2026-042 generated, staging server deployed, and private Slack channel created.",
        emailSubject: "🎉 Welcome to Johnson Softwares - Payment Confirmed & Project Kickoff",
        emailSnippet: "Agreement confirmed! Your designated Tech Lead has provisioned your staging environment. Sprint 1 architecture review begins Monday.",
        metricLabel: "Client Satisfaction",
        metricValue: "100%",
      },
    ],
  },
  {
    id: "logistics",
    name: "Logistics & Supply Chain",
    icon: Truck,
    color: "from-amber-500 to-orange-500",
    leadSample: {
      client: "Marcus Vance",
      company: "Apex Freight Solutions",
      inquiry: "Real-Time Fleet Dispatch & WhatsApp Driver Telemetry ERP",
      dealSize: "$35,000",
    },
    stages: [
      {
        title: "Instant RFQ Intake",
        badge: "⚡ Fleet RFQ Logged",
        actionDescription: "Inquiry parsed with freight volume tags. Assigned priority HOT based on 250-vehicle fleet scale requirement.",
        emailSubject: "Fleet Telemetry Inquiry Confirmed - Johnson Softwares",
        emailSnippet: "Hello Marcus, we received your RFQ for Real-Time Fleet Dispatch. Our logistics systems engineering squad is analyzing route API constraints...",
        metricLabel: "Lead Capture",
        metricValue: "Instant (0 dropped)",
      },
      {
        title: "Fleet Scope Discovery",
        badge: "📅 Telemetry Alignment",
        actionDescription: "Auto-dispatched GPS hardware integration checklist and dispatch protocol questionnaire directly to the operations head.",
        emailSubject: "Next Steps: Logistics Telemetry & Architecture Discovery",
        emailSnippet: "Before our discovery call, we prepared an overview of sub-second GPS webhooks, offline caching for highway routes, and driver WhatsApp alerts...",
        metricLabel: "Admin Time Saved",
        metricValue: "14 hrs/wk",
      },
      {
        title: "Custom ERP Proposal",
        badge: "📄 Multi-Depot SOW",
        actionDescription: "Generated architectural proposal with automated driver billing modules and live Google Maps fleet routing engine.",
        emailSubject: "Engineering Proposal: Apex Freight Operations Portal",
        emailSnippet: "Your fixed-scope estimate includes Phase 1 Driver Mobile App, Phase 2 Central Dispatch Board, and Phase 3 Accounting Sync...",
        metricLabel: "Quote Turnaround",
        metricValue: "Within 24 Hours",
      },
      {
        title: "Contract Won & Fleet Provisioning",
        badge: "🎉 Project Active",
        actionDescription: "Agreement executed. Dispatched staging login and provisioned high-throughput message broker on VPS cluster.",
        emailSubject: "🎉 Project Kickoff: Apex Freight Telemetry Infrastructure",
        emailSnippet: "Welcome Marcus! Your dedicated engineering pod has initialized your private repository and started simulator tests for 250 vehicle telemetry.",
        metricLabel: "ROI Multiplier",
        metricValue: "4.2x Efficiency",
      },
    ],
  },
  {
    id: "realestate",
    name: "Real Estate & Brokerage",
    icon: Home,
    color: "from-emerald-500 to-teal-500",
    leadSample: {
      client: "Sophia Lin",
      company: "Horizon Luxury Estates",
      inquiry: "Automated Lead Routing, WhatsApp Drips & Client Portal",
      dealSize: "$22,000",
    },
    stages: [
      {
        title: "Lead Attribution & Deduplication",
        badge: "⚡ Portal Sync Active",
        actionDescription: "Buyer inquiry captured via Meta Ads. Matched against existing buyer database to prevent duplicate agent assignment.",
        emailSubject: "Inquiry Received: Luxury Portfolio Access - Horizon Estates",
        emailSnippet: "Thank you Sophia, we have verified your request for high-yield property investment portfolios. An assigned senior advisor will connect shortly...",
        metricLabel: "Speed to Buyer",
        metricValue: "Under 30s",
      },
      {
        title: "Automated Tour Scheduling",
        badge: "📅 Viewing Sync",
        actionDescription: "Dispatched VIP property dossier link via email + WhatsApp trigger with 1-click private site visit booking.",
        emailSubject: "Private Viewing & Portfolio Scope - Horizon Luxury Estates",
        emailSnippet: "Your customized asset portfolio has been compiled. Select your preferred consultation or private site visit slot with our managing partner...",
        metricLabel: "Tour Booking Lift",
        metricValue: "+48%",
      },
      {
        title: "Investment Proposal",
        badge: "📄 Commercial Summary",
        actionDescription: "Delivered formal development proposal for a branded buyer portal with automated virtual walkthroughs and CRM integration.",
        emailSubject: "System Proposal: Horizon Luxury Client Portal Engine",
        emailSnippet: "Review your custom portal blueprint: automated KYC intake, digital document signing, and multi-broker commission tracking...",
        metricLabel: "Closing Speed",
        metricValue: "2.8x Faster",
      },
      {
        title: "Deal Closed & VIP Portal Live",
        badge: "🎉 Agreement Locked",
        actionDescription: "Agreement ratified. Initial deposit cleared, client VIP credentials provisioned, and broker pipeline activated.",
        emailSubject: "🎉 Welcome to Horizon Tech Ecosystem - Project Initiated",
        emailSnippet: "Contract confirmed! Your dedicated tech lead is setting up domain routing, SMS gateways, and MLS integration feeds.",
        metricLabel: "Conversion Rate",
        metricValue: "34% Uplift",
      },
    ],
  },
  {
    id: "health",
    name: "Healthcare & Clinics",
    icon: Stethoscope,
    color: "from-purple-500 to-pink-500",
    leadSample: {
      client: "Dr. Arvind Menon",
      company: "Apex Orthopedic Centers",
      inquiry: "HIPAA/EHR Patient Intake & Automated Telemedicine Portal",
      dealSize: "$32,000",
    },
    stages: [
      {
        title: "Secure Patient Intake",
        badge: "⚡ Encrypted Record",
        actionDescription: "Consultation inquiry ingested with zero data leakage. Automatically tagged with HIPAA/compliance security flags.",
        emailSubject: "Inquiry Received: Clinic Digital Infrastructure - Johnson Softwares",
        emailSnippet: "Hello Dr. Menon, we have registered your clinic automation consultation inquiry in our secure queue. Our senior healthtech lead is reviewing...",
        metricLabel: "Security Rating",
        metricValue: "100% Compliant",
      },
      {
        title: "Clinical Workflow Discovery",
        badge: "📅 Telehealth Scoping",
        actionDescription: "Dispatched structured EHR integration checklist (HL7/FHIR compatibility, slot scheduling, and SMS appointment reminders).",
        emailSubject: "Clinical Architecture & Requirement Gathering - Johnson Softwares",
        emailSnippet: "To prepare for our discovery consultation, we have assembled our standard compliance architecture for digital intake and payment workflows...",
        metricLabel: "No-Show Reduction",
        metricValue: "-62% Rate",
      },
      {
        title: "Compliance & Architecture SOW",
        badge: "📄 EHR SOW Prepared",
        actionDescription: "Delivered comprehensive proposal outlining encrypted database schemas, role-based doctor permissions, and patient dashboard.",
        emailSubject: "Technical Proposal: Apex Orthopedic Patient Portal",
        emailSnippet: "Our milestone plan includes: Milestone 1 HIPAA Encrypted DB, Milestone 2 Doctor Scheduling Engine, Milestone 3 Mobile Patient Intake...",
        metricLabel: "Admin Savings",
        metricValue: "28 hrs/wk",
      },
      {
        title: "Implementation Kickoff",
        badge: "🎉 Partnership Confirmed",
        actionDescription: "Agreement executed. Sandbox environment provisioned with mock patient records for doctors to test initial workflows.",
        emailSubject: "🎉 Project Initiated: Apex Orthopedic Infrastructure",
        emailSnippet: "Welcome Dr. Menon! Your engineering squad is ready. We have begun configuring the secure private cloud cluster and EHR adapter.",
        metricLabel: "Clinic Efficiency",
        metricValue: "+45% Output",
      },
    ],
  },
];

export default function CrmAutomationSimulator() {
  const [selectedIndustry, setSelectedIndustry] = useState<string>("tech");
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const activeIndustry = INDUSTRIES.find((i) => i.id === selectedIndustry) || INDUSTRIES[0];
  const activeStage = activeIndustry.stages[currentStep];

  // Auto-play interval
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isPlaying) {
      timer = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % activeIndustry.stages.length);
      }, 3500);
    }
    return () => clearInterval(timer);
  }, [isPlaying, activeIndustry.stages.length]);

  const handleNextStep = () => {
    setIsPlaying(false);
    setCurrentStep((prev) => (prev + 1) % activeIndustry.stages.length);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentStep(0);
  };

  return (
    <div className="w-full relative rounded-3xl bg-slate-900/90 dark:bg-slate-950/90 border border-slate-700/80 dark:border-slate-800/80 shadow-[0_20px_70px_rgba(37,99,235,0.18)] backdrop-blur-2xl overflow-hidden text-left">
      {/* Top Header Bar */}
      <div className="px-5 py-4 border-b border-slate-800 flex flex-wrap items-center justify-between gap-3 bg-slate-950/60">
        <div className="flex items-center gap-2.5">
          <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-300">
            Interactive Live Simulator
          </span>
          <span className="hidden sm:inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/20 text-primary border border-primary/30">
            Autonomous Pipeline Engine
          </span>
        </div>

        {/* Play / Reset Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
              isPlaying
                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                : "bg-primary/20 text-primary hover:bg-primary/30 border border-primary/40"
            }`}
          >
            {isPlaying ? (
              <>
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                Pause Auto-Play
              </>
            ) : (
              <>
                <Play className="w-3 h-3 fill-current" />
                Auto-Simulate
              </>
            )}
          </button>
          <button
            onClick={handleReset}
            title="Reset Simulator"
            className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Industry Tabs */}
      <div className="px-5 pt-3.5 pb-2 border-b border-slate-800/80 bg-slate-900/40 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        {INDUSTRIES.map((ind) => {
          const Icon = ind.icon;
          const isActive = ind.id === selectedIndustry;
          return (
            <button
              key={ind.id}
              onClick={() => {
                setSelectedIndustry(ind.id);
                setCurrentStep(0);
                setIsPlaying(false);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-2 whitespace-nowrap transition-all ${
                isActive
                  ? "bg-slate-800 text-white shadow-md border border-slate-700 font-bold"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${isActive ? "text-primary" : "text-slate-400"}`} />
              <span>{ind.name}</span>
            </button>
          );
        })}
      </div>

      {/* Main Interactive Stage Body */}
      <div className="p-5 space-y-5">
        {/* Step Progression Visual Stepper */}
        <div className="grid grid-cols-4 gap-2 relative">
          {activeIndustry.stages.map((stage, idx) => {
            const isCompleted = idx < currentStep;
            const isCurrent = idx === currentStep;
            return (
              <button
                key={idx}
                onClick={() => {
                  setCurrentStep(idx);
                  setIsPlaying(false);
                }}
                className={`p-2.5 rounded-xl text-left transition-all relative overflow-hidden border ${
                  isCurrent
                    ? "bg-primary/15 border-primary/50 text-white shadow-lg ring-1 ring-primary/40"
                    : isCompleted
                    ? "bg-slate-800/50 border-emerald-500/30 text-slate-300"
                    : "bg-slate-900/40 border-slate-800 text-slate-500 hover:bg-slate-800/30"
                }`}
              >
                {/* Progress highlight bar */}
                {isCurrent && (
                  <motion.div
                    layoutId="activeTabIndicator"
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary to-cyan-400"
                  />
                )}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
                    Step 0{idx + 1}
                  </span>
                  {isCompleted ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  ) : isCurrent ? (
                    <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
                  ) : null}
                </div>
                <div className="text-xs font-bold truncate">
                  {stage.title}
                </div>
              </button>
            );
          })}
        </div>

        {/* Live Simulation Canvas Card */}
        <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800/90 relative overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 pb-3 border-b border-slate-800/60">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase text-slate-400">
                  Simulated Inbound Deal
                </span>
                <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                  {activeIndustry.leadSample.dealSize} Contract
                </span>
              </div>
              <p className="text-sm font-bold text-white mt-0.5">
                {activeIndustry.leadSample.client} • {activeIndustry.leadSample.company}
              </p>
            </div>
            <div className="px-2.5 py-1 rounded-full text-xs font-bold bg-primary/20 text-cyan-300 border border-primary/30 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
              {activeStage.badge}
            </div>
          </div>

          {/* Action Description */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
              Automated System Action:
            </h4>
            <p className="text-xs sm:text-sm text-slate-200 leading-relaxed font-medium">
              {activeStage.actionDescription}
            </p>
          </div>

          {/* Real-time Email Preview Snippet */}
          <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 text-xs font-sans">
            <div className="flex items-center justify-between text-slate-400 pb-2 mb-2 border-b border-slate-800/80">
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary" />
                <span className="font-semibold text-slate-300">Live Client Dispatch Preview</span>
              </div>
              <span className="text-[10px] text-emerald-400 font-mono font-bold">● Delivered 250 OK</span>
            </div>
            <p className="font-bold text-white text-xs mb-1 truncate">
              {activeStage.emailSubject}
            </p>
            <p className="text-slate-400 text-xs italic leading-relaxed">
              &ldquo;{activeStage.emailSnippet}&rdquo;
            </p>
          </div>
        </div>

        {/* Live ROI Performance Impact Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">
              <Clock className="w-3 h-3 text-cyan-400" />
              <span>Speed to Lead</span>
            </div>
            <div className="text-base font-extrabold text-cyan-300">
              &lt; 45 Seconds
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">vs. 4-12 hrs manual</div>
          </div>

          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              <span>Lead Leakage</span>
            </div>
            <div className="text-base font-extrabold text-emerald-300">
              0% Dropped
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">Instant DB logging</div>
          </div>

          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">
              <TrendingUp className="w-3 h-3 text-amber-400" />
              <span>Deal Velocity</span>
            </div>
            <div className="text-base font-extrabold text-amber-300">
              3.2x Faster
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">Auto-followups</div>
          </div>

          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">
              <Trophy className="w-3 h-3 text-purple-400" />
              <span>Monthly SaaS Cost</span>
            </div>
            <div className="text-base font-extrabold text-purple-300">
              $0 / month
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">100% owned IP</div>
          </div>
        </div>

        {/* Bottom Interactive Conversion Bar */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-800/80">
          <div className="text-xs text-slate-400 text-center sm:text-left">
            <span className="font-bold text-white">Want this exact engine for your company?</span> Custom built & integrated in 10 days.
          </div>
          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <button
              onClick={handleNextStep}
              className="flex-1 sm:flex-none px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
            >
              Advance Stage
              <ArrowRight className="w-3 h-3" />
            </button>
            <Link
              href="/contact?service=automate"
              className="flex-1 sm:flex-none px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-400 text-white shadow-md flex items-center justify-center gap-1.5 transition-all whitespace-nowrap"
            >
              Build My Engine
              <Sparkles className="w-3.5 h-3.5" />
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}
